import styled from '@emotion/styled';
import queryString from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { getSortedLanguages } from 'src/modules/languages/selectors/languageSelectors';
import {
    getLanguageByLocale,
    getLanguageNameByLocale,
    getPreferredLanguage,
} from 'src/shared/helpers/getPreferredLanguage';
import { Language } from 'src/modules/languages/models/language';
import logoImg from 'src/public/img/mh-logo-text.svg';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import {
    BodyTextMedium,
    Button,
    ButtonGroup,
    CalloutInline,
    HeadingText60,
    Option,
    Select,
} from '@modernhealth/vital-components';
import { Http } from 'src/shared/services/Http';
import { mediaQueries } from 'src/shared/styles/variables';
import leftMountains from 'src/shared/svg/welcome-magic-link/left-mountains.svg';
import rightMountains1 from 'src/shared/svg/welcome-magic-link/right-mountains1.svg';
import rightMountains2 from 'src/shared/svg/welcome-magic-link/right-mountains2.svg';
import stars from 'src/shared/svg/welcome-magic-link/sparkling-stars.svg';
import { isSSOFlowRegistrationSource } from 'src/shared/helpers/isSSOFlowRegistrationSource';

import { RegistrationPageTestId } from './testIds';
import { captureException } from '@sentry/browser';
import { config } from 'src/config/config';

const Container = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
});

const LeftMountains = styled.div({
    bottom: 0,
    left: 0,
    position: 'absolute',
    [mediaQueries.md]: {
        display: 'none',
    },
});

const RightMountain = styled.div({
    textAlign: 'right',
    bottom: 0,
    right: 0,
    position: 'absolute',
});

const Stars = styled.div({
    position: 'absolute',
});

const ContentContainer = styled.div({
    position: 'relative',
    bottom: 244,
    margin: '0 auto',
    maxWidth: 550,
});

const TextContainer = styled.div({
    marginBottom: 40,
});

const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '15em',
    cursor: 'pointer',
    marginRight: 12,
});

const ContentRow = styled.div({
    display: 'flex',
    justifyContent: 'center',
});

interface ButtonsContainerProps {
    isSingleButton: boolean;
}

const ButtonsContainer = styled.div<ButtonsContainerProps>(({ isSingleButton }) => {
    return {
        width: `${isSingleButton ? '255px' : '550px'} !important`,
        display: isSingleButton ? 'flex' : 'block',
    };
});

const LanguageSelectionWrapper = styled.div({
    marginBottom: 40,
    width: '100%',
});

const CalloutWrapper = styled.div({
    marginTop: 10,
});

interface Response {
    firstName: string;
    lastName: string;
    email: string;
    birthDate: string;
    dateOfBirth: string;
    token: string;
    uidb64: string;
    companyCode: string;
    ssoEnabled: boolean;
    languagePreference: string;
    companyName: string;
}

export enum RegistrationErrorDetails {
    ALREADY_REGISTERED = 'Member has already registered an account',
    EXISTING_ACCOUNT = 'Member already has an account',
    INELIGIBLE = 'Person ineligible',
    SSO_ONLY = 'Member can only register through SSO',
    TOKEN_EXPIRED = 'Token expired',
}

export const RegistrationPage: React.FC = () => {
    const params = queryString.parse(window.location.search);
    const dispatch = useDispatch<Dispatch>();
    const { i18n, t } = useTranslation();
    const { allowSelectableLanguagePreference } = useFlags();
    const location = useLocation<Response>();
    const user = useSelector(getUserInformation);
    const languagesOptions = useSelector(getSortedLanguages);
    const [firstName, setFirstName] = useState(location.state?.firstName || '');
    const [lastName, setLastName] = useState(location.state?.lastName || '');
    const [email, setEmail] = useState(location.state?.email || '');
    const [ssoEnabled, setSsoEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [birthDate, setBirthDate] = useState(
        location.state?.dateOfBirth || location.state?.birthDate || '',
    );
    const [companyCode, setCompanyCode] = useState(location.state?.companyCode || '');
    const [companyName, setCompanyName] = useState(location.state?.companyName || '');
    const [shortLengthToken, setShortLengthToken] = useState('');
    const [languagePreference, setLanguagePreference] = useState<Option>();
    // This page is also used for Dependent Registration, using invite uuid to get the dependent data
    const isDependentInvitationFlow = location.pathname.includes('dependent-invitation');
    const uidb64 = params.uidb64 || undefined;
    const token = params.token || undefined;
    const isSSOFlow = isSSOFlowRegistrationSource(user.registrationSource);

    const updateUserInformation = async () => {
        // We need to update the member data after passing the SSO Flow to show this page correctly
        if (firstName === '') {
            await dispatch.user.fetchUserInfo();
        }
    };

    const getStarted = () => {
        dispatch.user.saveUserInfo({
            uuid: `registered-user-${new Date().getTime()}`,
            company: companyName,
        });

        navigationService.navigate('/consent-form-page', {
            token: shortLengthToken,
            companyCode,
            firstName,
            lastName,
            email,
            birthDate,
            uidb64,
            languagePreference: languagePreference?.value,
            isSSOFlow,
        });
    };

    const leftButtonLabel = params.token
        ? t('auth:registration_page.button.sign_up_with_single_sign_on')
        : t('auth:login_form.back');

    const redirectLeftButton = () => {
        if (params.token) {
            navigationService.navigate('/single-sign-on');
        } else {
            navigationService.navigate('/registration-basics', {
                firstName,
                lastName,
                birthDate,
                email,
                companyCode,
            });
        }
    };

    const hitTokenApiEndpoint = async () => {
        const http: Http = new Http();
        const pk = token || uidb64 || '';
        const url = `accounts/check-signuptoken/${pk}`;

        if (!isSSOFlow && pk) {
            try {
                const response = await http.GET_UNAUTHENTICATED<Response>(url);

                setFirstName(response.firstName);
                setLastName(response.lastName || '');
                setEmail(response.email || '');
                setBirthDate(response.dateOfBirth || '');
                setShortLengthToken(response.token);
                setCompanyCode(response.companyCode);
                setCompanyName(response.companyName);
                setSsoEnabled(response.ssoEnabled);
                setLoading(false);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                const detail = err?.body?.detail;
                const timeBeforeNextToken = err?.body?.timeBeforeNextToken || 0;

                if (err?.code === 404) {
                    return navigationService.navigate('/non-existent-link');
                }

                switch (detail) {
                    case RegistrationErrorDetails.TOKEN_EXPIRED:
                        navigationService.navigate('/invalid-registration-token', {
                            token: params.token,
                            timeBeforeNextToken,
                        });
                        break;
                    case RegistrationErrorDetails.ALREADY_REGISTERED:
                        navigationService.navigate('/login', { existingAccount: true });
                        break;
                    case RegistrationErrorDetails.EXISTING_ACCOUNT:
                        navigationService.navigate('/login', { existingAccount: true });
                        break;
                    case RegistrationErrorDetails.INELIGIBLE:
                        navigationService.navigate('/ineligible');
                        break;
                    case RegistrationErrorDetails.SSO_ONLY:
                        window.location.replace(err.body.ssoUrl);
                        break;
                    default:
                        toastNotificationsService.renderErrorToast(config.messages.genericError);
                        captureException(err);
                        navigationService.navigate('/login');
                        break;
                }
            }
        } else {
            await updateUserInformation();
            setLoading(false);
        }
    };

    useEffect(() => {
        hitTokenApiEndpoint();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!allowSelectableLanguagePreference) return;

        dispatch.languages.fetchLanguages();
    }, [allowSelectableLanguagePreference, dispatch.languages]);

    useEffect(() => {
        if (
            !allowSelectableLanguagePreference ||
            (languagesOptions.length > 0 && languagePreference)
        )
            return;

        const languageName =
            location.state?.languagePreference ||
            getLanguageNameByLocale(getPreferredLanguage()) ||
            'English';
        const languageOption = languagesOptions.find(option => {
            return option.name === languageName;
        });
        if (languageOption) {
            setLanguagePreference({
                label: languageOption.displayName,
                value: languageOption.name,
            });
        }
    }, [
        allowSelectableLanguagePreference,
        languagesOptions,
        location.state?.languagePreference,
        languagePreference,
    ]);

    useEffect(() => {
        if (!allowSelectableLanguagePreference) return;

        i18n.changeLanguage(getPreferredLanguage(languagePreference?.value));
    }, [allowSelectableLanguagePreference, i18n, languagePreference]);

    useEffect(() => {
        // When onboarding a member who registered through SSO, they will already have an account created so we will need
        // to be sure we update their languagePreferences real-time as opposed to passing the languagePreference through
        // the onboarding flow then creating an account
        const canUpdateLanguagePreferences = isSSOFlow && user && allowSelectableLanguagePreference;

        if (!canUpdateLanguagePreferences) return;

        if (languagePreference && !user.languagePreferences.includes(languagePreference.value)) {
            dispatch.user.updateUserInfo({ languagePreferences: [languagePreference.value] });
        }
    }, [allowSelectableLanguagePreference, dispatch.user, isSSOFlow, languagePreference, user]);

    useEffect(() => {
        if (isSSOFlow && user && user.firstName !== firstName) {
            setFirstName(user.firstName);
        }
    }, [firstName, isSSOFlow, user]);

    const isSingleButtonDisplayed =
        isDependentInvitationFlow || isSSOFlow || (!!token && !ssoEnabled);

    const selectableLanguagesOptions = useMemo(() => {
        if (!allowSelectableLanguagePreference) return [];

        return languagesOptions.map((option: Language) => {
            return { label: option.displayName, value: option.name } as Option;
        }) as Option[];
    }, [allowSelectableLanguagePreference, languagesOptions]);

    const shouldShowLoadingSpinner =
        loading ||
        (allowSelectableLanguagePreference &&
            (languagesOptions.length === 0 || !languagePreference));

    return (
        <>
            {shouldShowLoadingSpinner ? (
                <CenteredSpinner />
            ) : (
                <>
                    <BrandImage
                        src={logoImg}
                        onClick={() => {
                            window.location.href = 'https://www.modernhealth.com';
                        }}
                    />
                    <Container>
                        <LeftMountains>
                            <img src={leftMountains} alt="" />
                        </LeftMountains>
                        <RightMountain>
                            <img src={rightMountains1} alt="" />
                        </RightMountain>
                        <RightMountain>
                            <img src={rightMountains2} alt="" />
                        </RightMountain>
                        <Stars>
                            <img src={stars} alt="" />
                            <ContentContainer>
                                <TextContainer>
                                    <HeadingText60
                                        centered
                                        fontColor="gray85"
                                        marginBottom={40}
                                        maxWidth="550px"
                                    >
                                        {t('auth:registration_page.title', { firstName })}
                                    </HeadingText60>
                                    <BodyTextMedium centered fontColor="gray65">
                                        {t('auth:registration_page.description')}
                                    </BodyTextMedium>
                                    {allowSelectableLanguagePreference && (
                                        <BodyTextMedium bold centered>
                                            {t('onboarding:registration.select_preferred_language')}
                                        </BodyTextMedium>
                                    )}
                                </TextContainer>

                                {allowSelectableLanguagePreference && !!languagePreference && (
                                    <ContentRow>
                                        <LanguageSelectionWrapper>
                                            <Select
                                                data-testid={
                                                    RegistrationPageTestId.LanguagePreferenceSelect
                                                }
                                                id="languagePreferences"
                                                options={selectableLanguagesOptions}
                                                label={t('dependents:form.label.language')}
                                                placeholder={t('dependents:form.label.language')}
                                                value={languagePreference.value}
                                                onChange={(option: Option) =>
                                                    setLanguagePreference(option)
                                                }
                                            />

                                            {!getLanguageByLocale(languagePreference.value) && (
                                                <CalloutWrapper>
                                                    <CalloutInline
                                                        heading="Partially Supported Language"
                                                        variant="default"
                                                    >
                                                        {t(
                                                            'onboarding:registration.partially_supported_language',
                                                            {
                                                                value: languagePreference.value,
                                                                label: languagePreference.label,
                                                            },
                                                        )}
                                                    </CalloutInline>
                                                </CalloutWrapper>
                                            )}
                                        </LanguageSelectionWrapper>
                                    </ContentRow>
                                )}

                                <ContentRow>
                                    <ButtonsContainer isSingleButton={isSingleButtonDisplayed}>
                                        {isSingleButtonDisplayed ? (
                                            <Button
                                                onClick={getStarted}
                                                variant="primary"
                                                size="large"
                                                fluid
                                            >
                                                {t('auth:registration_page.button.get_started')}
                                            </Button>
                                        ) : (
                                            <ButtonGroup>
                                                <Button
                                                    onClick={redirectLeftButton}
                                                    variant="secondary"
                                                    size="large"
                                                >
                                                    {leftButtonLabel}
                                                </Button>
                                                <Button onClick={getStarted} size="large">
                                                    {t('auth:registration_page.button.get_started')}
                                                </Button>
                                            </ButtonGroup>
                                        )}
                                    </ButtonsContainer>
                                </ContentRow>
                            </ContentContainer>
                        </Stars>
                    </Container>
                </>
            )}
        </>
    );
};
